import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";

import { MapsTokenResponse } from "@asd/customer-portal-api";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Button, Card, Typography, useTheme, Zoom } from "@mui/material";
import * as atlas from "azure-maps-control";
import { AuthenticationType, AzureMap, AzureMapPopup, AzureMapsContext } from "react-azure-maps";

import { AddressOption } from "~/components/shared/AddressPicker";
import { useMapsContext } from "~/providers/MapsProvider";
import { ProviderRegistrationForDisplay } from "~/utils/providerRegistrationHelpers";
import { FIND_PROVIDERS_TEXT, NO_PROVIDERS_TEXT } from "../CareProviders";
import { MapViewState } from "../useCareProvidersState";
import { MapCenterControl } from "./MapCenterControl";
import { getMapBoundsDistance } from "./mapHelpers";
import MapLegend from "./MapLegend";
import ProviderCard from "./ProviderCard";

import "azure-maps-control/dist/atlas.min.css";

export interface MapLocationType {
  option?: AddressOption | undefined;
  coordinates: [number, number];
}

interface MapViewProps {
  mapToken: MapsTokenResponse;
  mapViewState: MapViewState;
  hasCurrentSearch: boolean;
  hoverProvider: ProviderRegistrationForDisplay | null;
  setIsLoading: (isLoading: boolean) => void;
  setHoverProvider: (provider: ProviderRegistrationForDisplay | null) => void;
  setMapViewState: (patch: Partial<MapViewState>) => void;
  handleSearch: (searchDistance: number) => void;
}

const MapView: FC<MapViewProps> = ({
  mapToken,
  mapViewState,
  hasCurrentSearch,
  hoverProvider,
  setIsLoading,
  setHoverProvider,
  setMapViewState,
  handleSearch,
}) => {
  const { location, providers, selectedProvider, providerListScrollTop } = mapViewState;

  const theme = useTheme();
  const { getMapToken } = useMapsContext();
  const { mapRef, isMapReady } = useContext(AzureMapsContext);

  const [showRefreshButton, setShowRefreshButton] = useState(false);

  const skipInitialFlightTime = useRef(!!hasCurrentSearch);
  const previousMapState = useRef<{ center: number[]; zoom: number }>({ center: [0, 0], zoom: 0 });
  const customControlsRef = useRef<any[]>([]);
  const serviceLocationMarkerRef = useRef<atlas.HtmlMarker | null>(null);
  const markerManager = useRef<Map<string, atlas.HtmlMarker>>(new Map());
  const markerPrevState = useRef<{
    hover: ProviderRegistrationForDisplay | null;
    selected: ProviderRegistrationForDisplay | null;
  }>({
    hover: hoverProvider,
    selected: selectedProvider,
  });
  const providerListRef = useRef<HTMLDivElement>(null);
  const hasRestoredProviderListScroll = useRef(false);
  const selectedProviderRef = useRef(selectedProvider);

  selectedProviderRef.current = selectedProvider;

  const handleRefresh = () => {
    const mapBoundsDistance = getMapBoundsDistance(mapRef, location);
    if (!mapBoundsDistance) return;

    const { center, zoom, maxDistanceInMiles } = mapBoundsDistance;
    if (maxDistanceInMiles) {
      setHoverProvider(null);
      handleSearch(maxDistanceInMiles);
    }

    if (center && zoom) {
      previousMapState.current = { center, zoom };
    }

    setShowRefreshButton(false);
  };

  const markerColors = useMemo(() => {
    return {
      serviceLocation: theme.palette.secondary.main,
      providerBase: theme.palette.primary.main,
      hover: theme.palette.primary.light,
      selected: theme.palette.primary.dark,
      outOfBounds: {
        base: theme.palette.grey[600],
        hover: theme.palette.grey[500],
        selected: theme.palette.grey[700],
      },
    };
  }, [theme]);

  const providerList = useMemo(() => {
    return providers.map((provider: ProviderRegistrationForDisplay) => (
      <Box key={provider.registration.providerRegistrationId} id={`providerCard_${provider.registration.providerRegistrationId}`}>
        <ProviderCard
          isHovered={hoverProvider?.registration.providerRegistrationId === provider.registration.providerRegistrationId}
          isSelected={selectedProvider?.registration.providerRegistrationId === provider.registration.providerRegistrationId}
          onClick={() => {
            const isCurrentlySelected =
              selectedProvider?.registration.providerRegistrationId === provider.registration.providerRegistrationId;
            setMapViewState({ selectedProvider: isCurrentlySelected ? null : provider });
          }}
          onMouseEnter={() => setHoverProvider(provider)}
          onMouseLeave={() => setHoverProvider(null)}
          provider={provider}
        />
      </Box>
    ));
  }, [
    hoverProvider?.registration.providerRegistrationId,
    providers,
    selectedProvider?.registration.providerRegistrationId,
    setHoverProvider,
    setMapViewState,
  ]);

  useEffect(() => {
    if (!hasCurrentSearch || !mapRef || !isMapReady) {
      setShowRefreshButton(false);
      return;
    }

    let onMoveEnd = (_e: atlas.MapEvent) => {};

    const { center, zoom } = mapRef.getCamera();
    if (center && zoom) {
      previousMapState.current = { center, zoom };

      onMoveEnd = (e) => {
        // If the event was not triggered by the user, skip the move action
        if (!e.originalEvent) return;

        const { center: newCenter, zoom: newZoom } = mapRef.getCamera();
        if (newCenter && newZoom) {
          const distanceDiff = atlas.math.getDistanceTo(previousMapState.current.center, newCenter);
          const zoomDiff = Math.abs(previousMapState.current.zoom - newZoom);

          if (distanceDiff > 500 || zoomDiff > 0.5) {
            setShowRefreshButton(true);
          }
        }
      };
    }

    mapRef.events.add("moveend", onMoveEnd);
    return () => mapRef.events.remove("moveend", onMoveEnd as any);
  }, [mapRef, isMapReady, hasCurrentSearch]);

  useEffect(() => {
    if (serviceLocationMarkerRef.current) {
      mapRef?.markers.remove(serviceLocationMarkerRef.current);
    }

    if (mapRef && isMapReady) {
      const { option, coordinates } = location;
      if (option && coordinates) {
        const newMarker = new atlas.HtmlMarker({
          position: coordinates,
          color: markerColors.serviceLocation,
          text: "★",
        });

        serviceLocationMarkerRef.current = newMarker;
        mapRef.markers.add(newMarker);

        // Force this marker to the top
        const element = newMarker.getElement();
        if (element?.parentNode) {
          element.parentNode.appendChild(element);
        }

        mapRef.setCamera({
          center: coordinates,
          zoom: 12,
          type: "fly",
          duration: skipInitialFlightTime.current ? 0 : 1000,
        });
      }

      skipInitialFlightTime.current = false;
    }
  }, [location, mapRef, isMapReady, markerColors]);

  useEffect(() => {
    if (!mapRef) return;

    markerManager.current.forEach((marker) => mapRef.markers.remove(marker));
    markerManager.current.clear();

    const pinsOutOfBounds: any[] = [];
    const pinsWithinBounds: any[] = [];

    providers.forEach((provider) => {
      const outOfBounds = (provider.registration.travelDistance ?? 0) <= (provider.distance ?? 0);
      const marker = new atlas.HtmlMarker({
        position: [provider.longitude ?? 0, provider.latitude ?? 0],
        zIndex: 1,
        color: outOfBounds ? markerColors.outOfBounds.base : markerColors.providerBase,
        text: outOfBounds ? "⊘" : "⏺",
      });

      mapRef.events.add("click", marker, () => {
        const element = document.getElementById(`providerCard_${provider.registration.providerRegistrationId}`);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        setMapViewState({ selectedProvider: provider });
      });
      mapRef.events.add("mouseenter", marker, () => setHoverProvider(provider));
      mapRef.events.add("mouseleave", marker, () => setHoverProvider(null));

      (outOfBounds ? pinsOutOfBounds : pinsWithinBounds).push(marker);
      markerManager.current.set(provider.registration.providerRegistrationId!, marker);
    });

    // Add the out of bounds markers first so that in bounds markers will be on top if they overlap
    pinsOutOfBounds.forEach((marker) => mapRef.markers.add(marker));
    pinsWithinBounds.forEach((marker) => mapRef.markers.add(marker));

    // Bring the service location marker to the top
    if (serviceLocationMarkerRef.current) {
      const element = serviceLocationMarkerRef.current.getElement();
      if (element?.parentNode) {
        element.parentNode.appendChild(element);
      }
    }

    // Restore selected marker style on remount
    const currentSelectedProvider = selectedProviderRef.current;
    if (currentSelectedProvider) {
      const selectedMark = markerManager.current.get(currentSelectedProvider.registration.providerRegistrationId ?? "");
      if (selectedMark) {
        const outOfBounds = (currentSelectedProvider.registration.travelDistance ?? 0) <= (currentSelectedProvider.distance ?? 0);
        selectedMark.setOptions({
          color: outOfBounds ? markerColors.outOfBounds.selected : markerColors.selected,
        });
        const element = selectedMark.getElement();
        if (element?.parentNode) {
          element.parentNode.appendChild(element);
        }
      }
    }

    markerPrevState.current.selected = currentSelectedProvider;
  }, [mapRef, providers, setHoverProvider, setMapViewState, markerColors]);

  useEffect(() => {
    if (mapRef && isMapReady) {
      if (customControlsRef.current.length > 0) return;

      const centerBtn = new MapCenterControl(() => {
        mapRef.setCamera({ center: location.coordinates, zoom: 12, type: "fly" });
      });
      const zoomCtrl = new atlas.control.ZoomControl();

      customControlsRef.current = [centerBtn, zoomCtrl];
      mapRef.controls.add(customControlsRef.current, {
        position: atlas.ControlPosition.BottomLeft,
      });
    }

    return () => {
      if (mapRef && customControlsRef.current.length > 0) {
        mapRef.controls.remove(customControlsRef.current);
        customControlsRef.current = [];
      }
    };
  }, [mapRef, isMapReady, location.coordinates]);

  useEffect(() => {
    if (markerPrevState.current.hover?.registration.providerRegistrationId !== hoverProvider?.registration.providerRegistrationId) {
      const prevHoverMarker = markerManager.current.get(markerPrevState.current.hover?.registration?.providerRegistrationId ?? "");
      if (prevHoverMarker) {
        const prevHoverProvider = markerPrevState.current.hover;
        prevHoverMarker.setOptions({
          color:
            (prevHoverProvider?.registration.travelDistance ?? 0) <= (prevHoverProvider?.distance ?? 0)
              ? markerColors.outOfBounds.base
              : markerColors.providerBase,
        });
        prevHoverMarker.getElement().style.filter = "";
      }
      markerPrevState.current.hover = hoverProvider;
    }

    if (markerPrevState.current.selected?.registration.providerRegistrationId !== selectedProvider?.registration.providerRegistrationId) {
      const prevSelectedProvider = markerPrevState.current.selected;
      markerManager.current.get(markerPrevState.current.selected?.registration.providerRegistrationId ?? "")?.setOptions({
        color:
          (prevSelectedProvider?.registration.travelDistance ?? 0) <= (prevSelectedProvider?.distance ?? 0)
            ? markerColors.outOfBounds.base
            : markerColors.providerBase,
      });
      markerPrevState.current.selected = selectedProvider;
    }

    const hoverMark = markerManager.current.get(hoverProvider?.registration.providerRegistrationId ?? "");
    hoverMark?.setOptions({
      color:
        (hoverProvider?.registration.travelDistance ?? 0) <= (hoverProvider?.distance ?? 0)
          ? markerColors.outOfBounds.hover
          : markerColors.hover,
    });
    if (hoverMark) {
      hoverMark.getElement().style.filter = "drop-shadow(2px 4px 6px black)";
    }

    const selectedMark = markerManager.current.get(selectedProvider?.registration.providerRegistrationId ?? "");
    selectedMark?.setOptions({
      color:
        (selectedProvider?.registration.travelDistance ?? 0) <= (selectedProvider?.distance ?? 0)
          ? markerColors.outOfBounds.selected
          : markerColors.selected,
    });

    // Bring the selected marker to the top
    if (selectedMark) {
      const element = selectedMark.getElement();
      if (element?.parentNode) {
        element.parentNode.appendChild(element);
      }
    }

    // Bring the service location marker to the top
    if (serviceLocationMarkerRef.current) {
      const element = serviceLocationMarkerRef.current.getElement();
      if (element?.parentNode) {
        element.parentNode.appendChild(element);
      }
    }
  }, [hoverProvider, selectedProvider, markerColors]);

  useEffect(() => {
    const el = providerListRef.current;
    if (!el) return;

    if (!hasRestoredProviderListScroll.current && providerListScrollTop) {
      el.scrollTop = providerListScrollTop;
      hasRestoredProviderListScroll.current = true;
    }

    const handleScroll = () => setMapViewState({ providerListScrollTop: el.scrollTop });
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [providerList]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Box display="grid" gridTemplateColumns="1fr 3fr" height="100%">
      <Card
        ref={providerListRef}
        elevation={2}
        sx={[
          {
            m: 1,
            mr: 0.5,
            p: 1,
            gap: 1,
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
          },
          !providers.length && { justifyContent: "center" },
        ]}
      >
        {!hasCurrentSearch && <Typography textAlign="center">{FIND_PROVIDERS_TEXT}</Typography>}
        {!!hasCurrentSearch && !providers.length && <Typography textAlign="center">{NO_PROVIDERS_TEXT}</Typography>}
        {providerList}
      </Card>
      <Card
        elevation={2}
        sx={{
          m: 1,
          position: "relative",
          "& .atlas-map .popup-content-container": {
            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.3)",
          },
          "& .azure-maps-control-container": {
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: "5px",
            "& .azure-maps-control-button": {
              width: "32px !important",
              height: "32px !important",
              backgroundColor: "white !important",
              borderRadius: "4px !important",
              boxShadow: "0 0 4px rgba(0,0,0,0.2) !important",
              border: "none !important",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              marginTop: "0 !important",
              "&:hover": { backgroundColor: "#f5f5f5 !important" },
            },
            "& .tooltiptext": {
              position: "absolute !important",
              left: "40px !important",
              backgroundColor: "rgba(0,0,0,0.8) !important",
              color: "#fff !important",
              padding: "4px 8px !important",
              borderRadius: "4px !important",
              fontSize: "12px !important",
              fontFamily: "Arial, sans-serif",
              whiteSpace: "nowrap",
              zIndex: 100,
              pointerEvents: "none",
              boxShadow: "0 2px 4px rgba(0,0,0,0.3)",
              display: "none !important",
              transition: "none !important",
              transform: "translateY(-50%) !important",
            },
            "& .azure-maps-control-button[aria-label='Recenter'] + .tooltiptext": { top: "16px !important" },
            "& .zoom-in + .tooltiptext": { top: "16px !important" },
            "& .zoom-out + .tooltiptext": { top: "53px !important" },
            "& .azure-maps-control-button:hover + .tooltiptext": { display: "block !important" },
            "& .atlas-zoom-control": {
              background: "none !important",
              border: "none !important",
              boxShadow: "none !important",
              overflow: "visible !important",
              position: "relative !important",
              display: "flex",
              flexDirection: "column",
              gap: "5px",
            },
          },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 20,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
          }}
        >
          <Zoom in={showRefreshButton}>
            <Button
              color="secondary"
              onClick={handleRefresh}
              startIcon={<SearchIcon />}
              sx={{
                borderRadius: 5,
                textTransform: "none",
                boxShadow: 3,
                "&:hover": { boxShadow: 8 },
              }}
              variant="contained"
            >
              Search this area
            </Button>
          </Zoom>
        </Box>
        <MapLegend markerColors={markerColors} />
        <AzureMap
          options={{
            authOptions: {
              authType: AuthenticationType.anonymous,
              clientId: mapToken.clientId,
              getToken: (resolve, reject) => {
                (async () => {
                  try {
                    setIsLoading(true);
                    const mapToken = await getMapToken();
                    if (mapToken.accessToken) {
                      resolve(mapToken.accessToken);
                    } else {
                      reject("No access token provided");
                    }
                  } finally {
                    setIsLoading(false);
                  }
                })();
              },
            },
            dragRotateInteraction: false,
            touchRotateInteraction: false,
            touchPitchInteraction: false,
            keyboardInteraction: false,
            cameraOptions: { pitch: 0, bearing: 0 },
            center: [-91.14614611039785, 43.051471023598786],
            zoom: 12,
            view: "Auto",
            showLogo: false,
            showFeedbackLink: false,
          }}
        >
          {hoverProvider ? (
            <AzureMapPopup
              options={{
                position: [hoverProvider.longitude ?? 0, hoverProvider.latitude ?? 0],
                pixelOffset: [0, -30],
                closeButton: false,
              }}
              popupContent={
                <div style={{ padding: 10, textAlign: "center", boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)" }}>
                  <p style={{ ...theme.typography.body1, fontWeight: "bold", margin: 0 }}>{hoverProvider.registration.name}</p>
                  <p style={{ ...theme.typography.subtitle1, margin: 0 }}>{hoverProvider.distance} Mile(s) away</p>
                </div>
              }
              isVisible
            />
          ) : undefined}
        </AzureMap>
      </Card>
    </Box>
  );
};

export default MapView;
